import type { FC, ReactElement } from 'react';
import { useCallback, useMemo, memo } from 'react';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import { CreativeAppName, CreativeMetric, CreativePaper } from '../../creatives';
import ContentPreview from './ContentPreview';
import { getPlatformIcon } from '../utils/getPlatformIcon';
import AgeReachChart from './AgeReachChart';
import ReactCountryFlag from 'react-country-flag';
import { LinkOutlined } from '@mui/icons-material';
import useFacebookAdsLibraryParams from '../hooks/useFacebookAdsLibraryParams';
import useEnqueueSnackbar from '../../../hooks/useEnqueueSnackbar';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getFBCreativeByIdOptions } from '../api/getFBCreativeById';
import { format } from 'date-fns';
import { getEnContent } from '../utils/getEnContent';
import copy from 'copy-to-clipboard';
import { isEmpty } from 'lodash-es';

const formatCompactNumber = (num: number) => {
  return new Intl.NumberFormat('en-US').format(num);
};

const InfoBlock: FC<{ title: string; content: string | number; additionalTitle?: ReactElement }> = ({
  title,
  content,
  additionalTitle,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: '150px' }}>
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Typography variant="subtitle2" color="text.secondary">
        {title}
      </Typography>
      {additionalTitle && <Box sx={{ ml: 1 }}>{additionalTitle}</Box>}
    </Box>
    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
      {!isNaN(Number(content)) ? formatCompactNumber(Number(content)) : content}
    </Typography>
  </Box>
);

interface IProps {
  adId: string;
}
interface Statistics {
  reach_18_24_stats: number;
  reach_25_34_stats: number;
  reach_35_44_stats: number;
  reach_45_54_stats: number;
  reach_55_64_stats: number;
  reach_65_plus_stats: number;
  reach_country: Record<string, number>;
  reach_female_stats: number;
  reach_male_stats: number;
  reach_unknown_stats: number;
}

const CreativeDetailsDialogContent: FC<IProps> = ({ adId }) => {
  const { startDate, endDate, network, region } = useFacebookAdsLibraryParams();
  const { onShowAlert } = useEnqueueSnackbar();
  const { data: creative } = useSuspenseQuery(
    getFBCreativeByIdOptions({
      id: adId,
      date_from: format(startDate, 'yyyy-MM-dd'),
      date_to: format(endDate, 'yyyy-MM-dd'),
      network,
      region,
    })
  );

  const [ad] = creative.ads;

  const statistics: Statistics = useMemo(
    () => ({
      reach_18_24_stats: ad.reach_18_24_stats_delta,
      reach_25_34_stats: ad.reach_25_34_stats,
      reach_35_44_stats: ad.reach_35_44_stats_delta,
      reach_45_54_stats: ad.reach_45_54_stats_delta,
      reach_55_64_stats: ad.reach_55_64_stats_delta,
      reach_65_plus_stats: ad.reach_65_plus_stats_delta,
      reach_country: ad.reach_country_delta,
      reach_female_stats: ad.reach_female_stats_delta,
      reach_male_stats: ad.reach_male_stats_delta,
      reach_unknown_stats: ad.reach_unknown_stats_delta,
    }),
    [
      ad.reach_18_24_stats_delta,
      ad.reach_25_34_stats,
      ad.reach_35_44_stats_delta,
      ad.reach_45_54_stats_delta,
      ad.reach_55_64_stats_delta,
      ad.reach_65_plus_stats_delta,
      ad.reach_country_delta,
      ad.reach_female_stats_delta,
      ad.reach_male_stats_delta,
      ad.reach_unknown_stats_delta,
    ]
  );

  const enTitle = getEnContent(ad.ad_creative_link_titles);
  const enBody = getEnContent(ad.ad_creative_bodies);

  const handleCopyScrapedLink = useCallback(() => {
    copy(ad.scraped_link);
    onShowAlert(Date.now(), 'Scraped link has been copied to clipboard!');
  }, [ad.scraped_link, onShowAlert]);

  const sortedCountryReach = useMemo(() => {
    if (isEmpty(statistics.reach_country)) return [];

    return Object.entries(statistics.reach_country).sort(([, countA], [, countB]) => countB - countA);
  }, [statistics]);

  return (
    <Box
      sx={() => ({
        display: 'flex',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        borderRadius: '8px',
      })}
    >
      {/* Left Side: Preview (35% width) */}
      <Box
        sx={{
          width: '35%', // Between 30-40%
          minWidth: '350px',
          bgcolor: 'background.default',
          borderRight: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden', p: 2 }}>
          <CreativePaper
            id={`fb-creative-${creative.ad_creative_id}-dialog`}
            sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <ContentPreview
              ad_library_url={ad.ad_library_url}
              video_preview_image_url={ad.video_preview_image_url}
              original_image_url={ad.original_image_url}
              video_sd_url={ad.video_sd_url}
              ad_id={ad.ad_id}
            />
          </CreativePaper>
        </Box>
      </Box>

      {/* Right Side: Details (Remaining width) */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            height: '100%',
            overflowY: 'auto', // Scroll only this part
          }}
        >
          {/* Header Section */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1,
                mb: 1,
              }}
            >
              <CreativeAppName>{ad.company || 'Unknown company'}</CreativeAppName>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {ad.publisher_platforms.map((pp) => {
                  const Icon = getPlatformIcon(pp);
                  return (
                    <Tooltip title={pp} key={pp}>
                      <Box>
                        <Icon />
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <CreativeMetric
                type={'reach'}
                title="Total reach delta"
                value={new Intl.NumberFormat('en-US').format(ad.total_reach_delta)}
              />
            </Box>
          </Box>

          {/* Content Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, pb: 2 }}>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  mb: 1,
                  fontWeight: 600,
                  wordBreak: 'break-word',
                  lineHeight: 1.3,
                }}
              >
                {enTitle}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  mb: 2,
                }}
              >
                {enBody}
              </Typography>

              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: 2 }}>
                <InfoBlock title="Page name" content={ad.page_name || 'Unknown page'} />
                <InfoBlock title="Branch" content={ad.branch || 'Unknown branch'} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Box sx={{ flex: 1 }}>
                <AgeReachChart
                  stats={{
                    reach_18_24_stats: statistics.reach_18_24_stats,
                    reach_25_34_stats: statistics.reach_25_34_stats,
                    reach_35_44_stats: statistics.reach_35_44_stats,
                    reach_45_54_stats: statistics.reach_45_54_stats,
                    reach_55_64_stats: statistics.reach_55_64_stats,
                    reach_65_plus_stats: statistics.reach_65_plus_stats,
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  p: 2,
                  flexWrap: 'wrap',
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                }}
              >
                <InfoBlock title="Reach 18-24 stats" content={statistics.reach_18_24_stats} />
                <InfoBlock title="Reach 25-34 stats" content={statistics.reach_25_34_stats} />
                <InfoBlock title="Reach 35-44 stats" content={statistics.reach_35_44_stats} />
                <InfoBlock title="Reach 45-54 stats" content={statistics.reach_45_54_stats} />
                <InfoBlock title="Reach 55-64 stats" content={statistics.reach_55_64_stats} />
                <InfoBlock title="Reach 65+ stats" content={statistics.reach_65_plus_stats} />
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                bgcolor: 'grey.50',
                borderRadius: 1,
                p: 2,
              }}
            >
              {/*<InfoBlock title="Target ages" content={ad.target_ages.map((a) => a).toString()} />*/}
              {/*<InfoBlock title="Target gender" content={ad.target_gender} />*/}
              <InfoBlock title="Male reach" content={statistics.reach_male_stats} />
              <InfoBlock title="Female reach" content={statistics.reach_female_stats} />
              <InfoBlock title="Unknown gender reach" content={statistics.reach_unknown_stats} />
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                p: 2,
                flexWrap: 'wrap',
                bgcolor: 'grey.50',
                borderRadius: 1,
              }}
            >
              {sortedCountryReach.map(([code, count]) => (
                <InfoBlock
                  key={code}
                  title={code}
                  content={count}
                  additionalTitle={<ReactCountryFlag countryCode={code} />}
                />
              ))}
            </Box>
          </Box>

          {/* Footer / Link Section */}
          <Box sx={{ pt: 2, mt: 'auto', borderTop: 1, borderColor: 'divider' }}>
            <Tooltip title={ad.scraped_link}>
              <Button
                onClick={handleCopyScrapedLink}
                variant="text"
                size="small"
                startIcon={<LinkOutlined />}
                sx={{
                  textTransform: 'none',
                  maxWidth: '100%',
                  color: 'text.secondary',
                  justifyContent: 'flex-start',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {ad.scraped_link}
                </Typography>
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default memo(CreativeDetailsDialogContent);
